/**
 * Enhanced Global Search Palette
 *
 * Spotlight-like search experience with:
 * - Command palette (/, >, @)
 * - Category prefix filters (p:, t:, r:, etc.)
 * - Ask Solomon AI (?)
 * - Rich preview panel
 * - Keyboard navigation
 * - Quick actions
 * - Smart suggestions
 */

import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  History,
  Clock,
  Sparkles,
  Command as CommandIcon,
  X,
  ChevronRight,
  Zap,
  Search,
  User,
  CheckSquare,
  MessageSquare,
  Calendar,
  Home,
  ArrowRight,
  HelpCircle,
  DollarSign,
  FileText,
  FolderKanban,
  Church,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEnhancedSearch, useSearchSuggestions, CATEGORY_PREFIXES, type EnhancedSearchResult } from "@/hooks/useEnhancedSearch";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { useSearchTelemetry } from "@/hooks/useSearchTelemetry";
import { getObjectRoute } from "@/hooks/useGlobalSearch";
import { EnhancedSearchResultItem, CompactSearchResultItem } from "./EnhancedSearchResultItem";
import { SearchPreviewPanel } from "./SearchPreviewPanel";
import { InlineSolomonResponse } from "./InlineSolomonResponse";
import { useSolomonContext } from "@/components/solomon/SolomonContext";
import { parseSearchQuery } from "@/lib/search-commands";
import { FOCUS_DELAY_MS } from "@/lib/timing-constants";

interface EnhancedGlobalSearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Prefix hints for quick reference
const PREFIX_HINTS = [
  { prefix: "p:", label: "People", icon: User },
  { prefix: "t:", label: "Tasks", icon: CheckSquare },
  { prefix: "r:", label: "Requests", icon: MessageSquare },
  { prefix: "@", label: "Pages", icon: ArrowRight },
  { prefix: "/", label: "Commands", icon: Zap },
  { prefix: "?", label: "Ask Solomon", icon: Sparkles },
];

const TOP_QUICK_FILTERS = [
  { prefix: "p:", label: "p:", icon: User },
  { prefix: "t:", label: "t:", icon: CheckSquare },
  { prefix: "r:", label: "r:", icon: MessageSquare },
  { prefix: "@", label: "@", icon: ArrowRight },
  { prefix: "/", label: "/", icon: Zap },
  { prefix: "?", label: "? Ask Solomon", icon: Sparkles },
];

function getCompactResultValue(result: EnhancedSearchResult): string {
  return `compact-${result.type}-${result.id}-${result.label}`;
}

// The cmdk value EnhancedSearchResultItem gives a result row. Keep the two in step: the palette's one
// selection is cmdk's value, and the preview pane and selected styling are looked up from it.
function getResultOptionValue(result: EnhancedSearchResult): string {
  return `${result.type}-${result.id}-${result.label}`;
}

// Filter tabs matching reference design
const FILTER_TABS: Array<{ id: string; label: string; icon: LucideIcon; prefix: string }> = [
  { id: "all", label: "All", icon: Search, prefix: "" },
  { id: "people", label: "People", icon: User, prefix: "p:" },
  { id: "requests", label: "Requests", icon: MessageSquare, prefix: "r:" },
  { id: "contributions", label: "Giving", icon: DollarSign, prefix: "" },
  { id: "services", label: "Services", icon: Church, prefix: "s:" },
  { id: "pages", label: "Pages", icon: FileText, prefix: "@" },
  { id: "actions", label: "Actions", icon: Zap, prefix: "/" },
];


// The "Go to Page" rows. Each is a cmdk option with its own onSelect; the preview pane shows one by
// looking its option value up in optionPreviewMap.
const GO_TO_PAGES = [
  { label: "Today", path: "/", icon: Home },
  { label: "People", path: "/people", icon: User },
  { label: "Tasks", path: "/tasks", icon: CheckSquare },
  { label: "Care Requests", path: "/requests", icon: MessageSquare },
  { label: "Finance", path: "/finance", icon: DollarSign },
  { label: "Calendar", path: "/calendar", icon: Calendar },
  { label: "Reports", path: "/reports", icon: FileText },
  { label: "Projects", path: "/projects", icon: FolderKanban },
];

const pageOptionValue = (path: string) => `go-to-page-${path}`;

const pagePreviewResult = (page: { label: string; path: string }): EnhancedSearchResult => ({
  id: `page-${page.path}`,
  score: 0,
  type: "command",
  category: "navigation",
  label: page.label,
  description: `Go to ${page.path}`,
  icon: "Navigation",
  command: {
    id: `nav-${page.path}`,
    label: page.label,
    category: "navigation",
    keywords: [page.label.toLowerCase()],
    icon: "Navigation",
    action: { type: "navigate", path: page.path },
  },
});

const RECENT_SEARCH_VALUE = /^recent-search-(\d+)-/;

export function EnhancedGlobalSearchPalette({ open, onOpenChange }: EnhancedGlobalSearchPaletteProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  const inputRef = useRef<HTMLInputElement>(null);
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
  const { setIsOpen: setSolomonPanelOpen } = useSolomonContext();

  // State
  const [query, setQuery] = useState("");
  // The one selection: the option cmdk highlights. cmdk 1.1.1 reports a highlight change through
  // onValueChange only when it is given `value`, so the palette holds it. The preview pane, the
  // selected styling, quick actions and Delete on a recent search all read it; Enter is cmdk's
  // own onSelect on the row, so there is no second index to keep in step.
  const [commandValue, setCommandValue] = useState("");
  const [searchStartTime, setSearchStartTime] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  // Parse query to detect Solomon mode
  const parsedQuery = useMemo(() => parseSearchQuery(query), [query]);
  const isSolomonMode = parsedQuery.prefix === "?";
  const solomonQuery = isSolomonMode ? parsedQuery.searchTerm : "";

  // Search data (disabled in Solomon mode)
  const {
    results,
    isLoading,
    isEmpty,
    hasRecords,
    hasCommands,
    activePrefix,
    prefixMeta,
  } = useEnhancedSearch({
    query,
    limit: 25,
    enabled: query.length >= 1 && !isSolomonMode,
    activeFilter: activeFilter !== "all" ? FILTER_TABS.find(t => t.id === activeFilter)?.prefix : undefined,
  });

  // Suggestions for empty state
  const { recentItems, myItems, quickCommands, isLoading: suggestionsLoading } = useSearchSuggestions();

  // Search history
  const { recentSearches, addToHistory, removeFromHistory, clearHistory } = useSearchHistory();

  // Telemetry
  const { trackSearch, trackSelect, trackAction } = useSearchTelemetry();

  // Track search start time
  useEffect(() => {
    if (query.length >= 2 && !searchStartTime) {
      setSearchStartTime(Date.now());
    } else if (query.length < 2) {
      setSearchStartTime(null);
    }
  }, [query, searchStartTime]);

  // Track search telemetry when results arrive — at most ONE event per
  // executed query (UAT 3.6). Guards:
  // - skip while the records query is still loading, otherwise a brand-new
  //   query is reported as zero_results before its fetch settles
  // - dedupe by query string via ref so re-renders (e.g. from the telemetry
  //   mutation itself) can never re-fire the event for the same query
  // The old version also fired an unsampled trackAction("zero_results") on
  // every render while results were empty, which created an endless
  // mutate -> re-render -> mutate POST loop that starved the next search.
  const lastTrackedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (query.length < 2 || isLoading) return;
    if (lastTrackedQueryRef.current === query) return;
    lastTrackedQueryRef.current = query;
    // trackSearch records eventType "zero_results" itself when the count is 0.
    trackSearch(query, results.length);
  }, [query, results, isLoading, trackSearch]);

  // Handle built-in actions
  const handleBuiltInAction = useCallback(
    (actionId: string) => {
      switch (actionId) {
        case "quick-add":
          window.dispatchEvent(new CustomEvent("open-quick-add"));
          break;
        case "refresh":
          queryClient.invalidateQueries();
          toast.success("Data refreshed");
          break;
        case "show-shortcuts":
          toast.info("Keyboard shortcuts: ⌘K to search, Arrow keys to navigate, Enter to select");
          break;
        case "feedback":
          window.open("mailto:feedback@forgehq.io?subject=Forge Feedback", "_blank");
          break;
        default:
          toast.info(`Action: ${actionId}`);
      }
    },
    [queryClient]
  );

  // Handle result selection
  const handleSelect = useCallback(
    (result: EnhancedSearchResult) => {
      // Track selection
      const timeToAction = searchStartTime ? Date.now() - searchStartTime : undefined;

      if (result.type === "record" && result.objectType && result.objectId) {
        trackSelect(result.objectType, result.objectId, query, timeToAction);
      }

      // Add to search history
      if (query.length >= 2) {
        addToHistory(query, results.length);
      }

      // Handle different result types
      if (result.type === "command" && result.command) {
        const action = result.command.action;
        switch (action.type) {
          case "navigate":
            navigate(action.path);
            break;
          case "create":
            navigate(action.path);
            break;
          case "action":
            handleBuiltInAction(action.actionId);
            break;
          case "external":
            window.open(action.url, "_blank");
            break;
        }
      } else if (result.type === "record" && result.objectType && result.objectId) {
        const route = getObjectRoute(result.objectType, result.objectId);
        navigate(route);
      }

      // Close the palette
      onOpenChange(false);
      setQuery("");
      setSearchStartTime(null);
    },
    [navigate, onOpenChange, query, results, addToHistory, trackSelect, searchStartTime, handleBuiltInAction]
  );

  // Handle quick actions on results
  const handleQuickAction = useCallback(
    async (result: EnhancedSearchResult, actionId: string) => {
      if (result.type !== "record" || !result.objectId) return;

      trackAction(actionId, query, result.objectType, result.objectId);

      switch (actionId) {
        case "complete":
          if (result.objectType === "task") {
            if (!organizationId) {
              toast.error("No organization selected");
              break;
            }
            // Mirrors src/pages/Tasks.tsx's completion toggle — without
            // completed_at, metrics keyed on it (e.g. Today's "tasks
            // cleared" pulse stat) never see completions made from here.
            // Preserve the existing timestamp when already done, so
            // re-triggering "Complete" on an old completed result doesn't
            // make it look like it was just cleared today.
            const wasAlreadyDone = result.status === "done";
            const { error } = await supabase
              .from("tasks")
              .update({
                status: "done",
                ...(wasAlreadyDone ? {} : { completed_at: new Date().toISOString() }),
              })
              .eq("organization_id", organizationId)
              .eq("id", result.objectId);
            if (error) {
              toast.error("Failed to complete task");
            } else {
              toast.success("Task completed!");
              queryClient.invalidateQueries({ queryKey: ["enhanced-search"] });
              queryClient.invalidateQueries({ queryKey: ["global-search"] });
            }
          }
          break;

        case "assign":
          const route = getObjectRoute(result.objectType!, result.objectId);
          navigate(`${route}?action=assign`);
          onOpenChange(false);
          setQuery("");
          break;

        case "message":
          if (result.objectType === "person") {
            navigate(`/people/${result.objectId}?composeMessage=true`);
            onOpenChange(false);
            setQuery("");
          }
          break;
      }
    },
    [navigate, onOpenChange, organizationId, query, queryClient, trackAction]
  );

  // Handle recent search selection
  const handleRecentSearchSelect = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    inputRef.current?.focus();
  }, []);

  // Handle expanding Solomon to full panel
  const handleExpandSolomon = useCallback(() => {
    // Close search and open Solomon panel with the current query
    onOpenChange(false);
    const currentSolomonQuery = solomonQuery;
    setQuery("");

    // Small delay to let search close first
    setTimeout(() => {
      // Dispatch event to open Solomon with the query as a draft
      window.dispatchEvent(
        new CustomEvent("open-solomon", { detail: { prompt: currentSolomonQuery } })
      );
    }, 100);
  }, [onOpenChange, solomonQuery]);

  // Keyboard navigation
  // Clears the selection and asks cmdk to highlight the first row with its own Home key, so the highlight
  // goes through cmdk's value path, which also moves its active descendant. Setting the first row's value
  // from here would leave aria-activedescendant on a row that is gone. No timer cleanup: clearing the
  // value re-renders, and after unmount the input ref is null so a late firing does nothing.
  const rehighlightFirst = useCallback(() => {
    setCommandValue("");
    setTimeout(() => {
      inputRef.current?.dispatchEvent(new KeyboardEvent("keydown", { key: "Home", bubbles: true }));
    }, 0);
  }, []);

  // Removes the recent search cmdk has highlighted (empty box only). Its option value carries the
  // index and the query; the query must still sit at that index, so a stale highlight after the
  // history shifted never removes a different entry.
  const removeHighlightedRecentSearch = useCallback(
    (e: React.KeyboardEvent): boolean => {
      const match = RECENT_SEARCH_VALUE.exec(commandValue);
      if (query !== "" || !match) return false;
      const search = recentSearches[Number(match[1])];
      if (!search || commandValue !== `recent-search-${match[1]}-${search.query}`) return false;
      e.preventDefault();
      removeFromHistory(search.query);
      // The row is gone, so drop the highlight and let cmdk pick again.
      rehighlightFirst();
      return true;
    },
    [query, commandValue, recentSearches, removeFromHistory, rehighlightFirst]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Tab":
          e.preventDefault();
          setActiveFilter((prev) => {
            const ids = FILTER_TABS.map((f) => f.id);
            const idx = ids.indexOf(prev);
            return ids[(idx + 1) % ids.length];
          });
          break;
        case "Delete":
          removeHighlightedRecentSearch(e);
          break;
        case "Escape":
          if (query) {
            e.preventDefault();
            setQuery("");
          }
          break;
        case "Backspace":
          // The key labelled Delete on a Mac keyboard reports Backspace, so it removes a highlighted
          // recent search too; with text in the box it stays ordinary text deletion.
          if (removeHighlightedRecentSearch(e)) break;
          if (query === "" && activePrefix) {
            // Clear prefix filter
            setQuery("");
          }
          break;
      }
    },
    [query, activePrefix, removeHighlightedRecentSearch]
  );

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setQuery("");
      setCommandValue("");
      setSearchStartTime(null);
      setActiveFilter("all");
      lastTrackedQueryRef.current = null;
    }
  }, [open]);

  // cmdk 1.1.1 can schedule selectedItemId before controlled selection reaches the DOM.
  // Its highlighted row and value remain correct, but the input/list can reference a removed
  // option or omit their active descendant after mounting or resetting. Derive only this ARIA
  // relationship from cmdk's rendered highlight; cmdk still owns selection and Enter dispatch.
  useEffect(() => {
    const input = inputRef.current;
    const list = listElement;
    if (!open || !input || !list) return;

    const syncActiveDescendant = () => {
      const selected = Array.from(list.querySelectorAll<HTMLElement>('[cmdk-item][aria-selected="true"]'))
        .find((item) => !item.closest("[hidden]"));
      const id = selected?.id;
      for (const element of [input, list]) {
        if (id) {
          if (element.getAttribute("aria-activedescendant") !== id) element.setAttribute("aria-activedescendant", id);
        } else if (element.hasAttribute("aria-activedescendant")) {
          element.removeAttribute("aria-activedescendant");
        }
      }
    };

    const observer = new MutationObserver(syncActiveDescendant);
    observer.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-selected", "aria-activedescendant", "id", "hidden"] });
    observer.observe(input, { attributes: true, attributeFilter: ["aria-activedescendant"] });
    syncActiveDescendant();
    return () => observer.disconnect();
  }, [open, listElement]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), FOCUS_DELAY_MS);
    }
  }, [open]);

  // Listen for global events
  useEffect(() => {
    const handleOpenSearch = (event: Event) => {
      event.preventDefault();
      onOpenChange(true);
    };
    const handleCloseSearch = () => onOpenChange(false);
    const handleOpenSearchWithQuery = (e: Event) => {
      const customEvent = e as CustomEvent<{ query?: string }>;
      if (customEvent.detail?.query) {
        setQuery(customEvent.detail.query);
      }
      onOpenChange(true);
    };

    window.addEventListener("open-search", handleOpenSearch);
    window.addEventListener("close-search", handleCloseSearch);
    window.addEventListener("open-search-with-query", handleOpenSearchWithQuery);
    return () => {
      window.removeEventListener("open-search", handleOpenSearch);
      window.removeEventListener("close-search", handleCloseSearch);
      window.removeEventListener("open-search-with-query", handleOpenSearchWithQuery);
    };
  }, [onOpenChange]);

  // Determine what to show
  const showRecentSearches = query.length === 0 && recentSearches.length > 0;
  const showSuggestions = query.length === 0;
  const showResults = query.length >= 1 && results.length > 0 && !isSolomonMode;
  const showEmpty = query.length >= 2 && !isLoading && results.length === 0 && !isSolomonMode;

  // Group results by category for display
  const groupedResults = useMemo(() => {
    if (!showResults) return null;

    const groups: Record<string, EnhancedSearchResult[]> = {};

    for (const result of results) {
      const groupKey = result.type === "command" ? "commands" : result.category;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(result);
    }

    return groups;
  }, [results, showResults]);

  const groupLabels: Record<string, string> = {
    commands: "Commands",
    person: "People",
    household: "Households",
    request: "Requests",
    task: "Tasks",
    project: "Projects",
    contribution: "Contributions",
    fund: "Funds",
    asset: "Assets",
    campaign: "Campaigns",
    pledge: "Pledges",
    event: "Events",
    service: "Services",
  };

  const shouldShowDesktopPane = showPreview || isSolomonMode;
  const suggestionPreviewMap = useMemo(() => {
    if (!showSuggestions) return new Map<string, EnhancedSearchResult>();

    const map = new Map<string, EnhancedSearchResult>();
    [...myItems, ...quickCommands, ...recentItems.slice(0, 5)].forEach((result) => {
      map.set(getCompactResultValue(result), result);
    });

    return map;
  }, [myItems, quickCommands, recentItems, showSuggestions]);

  const pagePreviewMap = useMemo(
    () => new Map(GO_TO_PAGES.map((page) => [pageOptionValue(page.path), pagePreviewResult(page)])),
    [],
  );

  // Everything the preview pane can show, by the cmdk option value of its row.
  const optionPreviewMap = useMemo(() => {
    const map = new Map<string, EnhancedSearchResult>(suggestionPreviewMap);
    pagePreviewMap.forEach((result, value) => map.set(value, result));
    results.forEach((result) => map.set(getResultOptionValue(result), result));
    return map;
  }, [suggestionPreviewMap, pagePreviewMap, results]);

  const paneResult = optionPreviewMap.get(commandValue) ?? null;

  // The selection is an option's identity (type, id and label), so it follows its row through a
  // reorder or refetch and needs no reseeding. When that option is no longer displayed (a rename, a
  // removed recent search, new results) it is cleared and cmdk re-highlights the first row.
  const displayedValues = useMemo(() => {
    if (query.length > 0) return new Set(results.map(getResultOptionValue));
    const values = new Set<string>([...suggestionPreviewMap.keys(), ...pagePreviewMap.keys()]);
    recentSearches.slice(0, 5).forEach((search, idx) => values.add(`recent-search-${idx}-${search.query}`));
    return values;
  }, [query.length, results, suggestionPreviewMap, pagePreviewMap, recentSearches]);
  useEffect(() => {
    if (commandValue === "" || displayedValues.has(commandValue)) return;
    rehighlightFirst();
  }, [commandValue, displayedValues, rehighlightFirst]);

  // Handle preview action
  const handlePreviewAction = useCallback(
    (action: string) => {
      if (paneResult) {
        handleQuickAction(paneResult, action);
      }
    },
    [paneResult, handleQuickAction]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(96vw,1120px)] max-w-[1120px] p-0 gap-0 overflow-hidden origin-top max-sm:left-0 max-sm:right-0 max-sm:top-[env(safe-area-inset-top)] max-sm:bottom-auto max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:h-[calc(100dvh_-_env(safe-area-inset-top))] max-sm:max-h-[calc(100dvh_-_env(safe-area-inset-top))] max-sm:rounded-none max-sm:flex max-sm:flex-col sm:top-[8%] sm:translate-y-0 sm:max-h-[84vh] data-[state=open]:animate-search-genie-in data-[state=closed]:animate-search-genie-out"
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search people, tasks, requests, giving records, services, pages, actions, and Solomon answers.
        </DialogDescription>

            <div
              className={cn(
                "flex flex-col max-sm:flex-1 max-sm:min-h-0 lg:flex-row lg:h-[min(78vh,720px)]",
                shouldShowDesktopPane ? "lg:divide-x lg:divide-border/70" : ""
              )}
            >
              {/* Main search area */}
              <div
                className={cn(
                  "flex-1 min-w-0 max-sm:min-h-0",
                  shouldShowDesktopPane ? "lg:basis-[64%]" : ""
                )}
              >
                <Command
                  className="rounded-none border-none bg-transparent flex h-full flex-col"
                  value={commandValue}
                  onValueChange={setCommandValue}
                  loop
                >
                  {/* Search input with prefix indicator */}
                  <div className="relative border-b border-border/60">
                    <div className="flex items-center justify-between px-5 pt-3 pb-1.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/60">Global Search</span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground/60">
                        <kbd className="px-1.5 py-0.5 rounded border border-border bg-background text-[10px] font-mono font-semibold text-foreground/80">Esc</kbd>
                        Close
                      </span>
                    </div>
                    <div className="flex items-center px-5 pb-2">
                      {activePrefix && prefixMeta && (
                        <Badge variant="secondary" className="mr-3 gap-1.5 text-xs font-medium">
                          {prefixMeta.label}
                          <button
                            onClick={() => setQuery("")}
                            className="ml-1 hover:text-destructive transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </Badge>
                      )}
                      <CommandInput
                        ref={inputRef}
                        placeholder={
                          isSolomonMode
                            ? "Ask Solomon anything..."
                            : activePrefix
                            ? `Search ${prefixMeta?.label.toLowerCase()}...`
                            : "Search people, requests, contributions, pages..."
                        }
                        value={query}
                        onValueChange={setQuery}
                        className="h-14 text-[15px] font-semibold flex-1"
                      />
                      {query && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => setQuery("")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                    {/* Filter tabs */}
                    <div className="flex items-center gap-1.5 px-5 py-2.5 border-b overflow-x-auto max-sm:px-4 max-sm:[scrollbar-width:none] max-sm:[-ms-overflow-style:none] max-sm:[&::-webkit-scrollbar]:hidden" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                      {FILTER_TABS.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveFilter(tab.id)}
                          className={cn(
                            "flex shrink-0 items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer leading-none",
                            activeFilter === tab.id
                              ? "bg-primary-tint text-primary-tint-foreground border border-primary-tint-border font-semibold"
                              : "border border-transparent hover:bg-muted/50"
                          )}
                          style={activeFilter !== tab.id ? { background: "rgba(0,0,0,0.03)", color: "var(--forge-text-muted)" } : undefined}
                        >
                          <tab.icon className="h-3.5 w-3.5 stroke-[2.25]" />
                          {tab.label}
                        </button>
                      ))}
                      <span className="ml-auto text-[11px] font-medium hidden sm:inline" style={{ color: "var(--forge-text-muted)" }}>Tab to switch filters</span>
                    </div>

                    <div className="hidden sm:block border-b px-5 py-2.5" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/60">Quick Filters</span>
                        {TOP_QUICK_FILTERS.map((filter) => (
                          <button
                            key={filter.prefix}
                            type="button"
                            tabIndex={-1}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              setQuery(filter.prefix);
                              inputRef.current?.focus();
                            }}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background px-2.5 py-1 text-[11px] font-semibold leading-none text-foreground/80 transition-colors hover:bg-muted"
                          >
                            <filter.icon className="h-3.5 w-3.5 stroke-[2.2]" />
                            <span className={filter.prefix === "?" ? "" : "font-mono"}>{filter.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                  <CommandList ref={setListElement} className="max-h-[60vh] overflow-auto max-sm:max-h-none max-sm:flex-1 max-sm:min-h-0 max-sm:pb-[env(safe-area-inset-bottom)] lg:max-h-none lg:flex-1 overscroll-contain">
                    {/* Loading state */}
                    {isLoading && query.length >= 2 && (
                      <div
                        role="status"
                        aria-live="polite"
                        aria-label="Searching"
                        className="flex items-center justify-center py-12 text-muted-foreground"
                      >
                        <Loader2 className="h-6 w-6 animate-spin mr-3" />
                        <span className="text-sm font-medium">Searching...</span>
                      </div>
                    )}

                    {/* Empty state with helpful hints */}
                    {showEmpty && (
                      <CommandEmpty>
                        <div className="flex flex-col items-center py-12 px-4">
                          <Search className="h-12 w-12 text-muted-foreground/30 mb-4" />
                          <p className="text-base font-semibold text-foreground mb-1">No results for "{query}"</p>
                          <p className="text-sm text-foreground/60 mb-6">
                            Try different keywords or use a prefix filter
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {PREFIX_HINTS.slice(0, 3).map((hint) => (
                              <Button
                                key={hint.prefix}
                                variant="outline"
                                size="sm"
                                className="text-sm gap-2 h-9 px-3.5 font-medium"
                                onClick={() => setQuery(hint.prefix)}
                              >
                                <hint.icon className="h-4 w-4" />
                                <span className="font-mono font-semibold">{hint.prefix}</span> {hint.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </CommandEmpty>
                    )}

                    {/* Recent searches */}
                    {showRecentSearches && (
                      <CommandGroup
                        heading={
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                              <History className="h-4 w-4" />
                              Recent Searches
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                clearHistory();
                              }}
                              className="text-xs text-foreground/60 hover:text-foreground font-semibold"
                            >
                              Clear
                            </button>
                          </div>
                        }
                      >
                        {recentSearches.slice(0, 5).map((search, idx) => (
                          <div key={`recent-${idx}`} className="relative group">
                            <CommandItem
                              value={`recent-search-${idx}-${search.query}`}
                              className="flex items-center gap-3 px-4 py-3 pr-12 cursor-pointer hover:bg-hover rounded-lg transition-colors"
                              onSelect={() => handleRecentSearchSelect(search.query)}
                            >
                              <History className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="flex-1 text-sm font-medium">{search.query}</span>
                              {search.resultCount > 0 && (
                                <Badge variant="secondary" className="text-xs font-medium">
                                  {search.resultCount}
                                </Badge>
                              )}
                            </CommandItem>
                            {/* Outside the option: a listbox option cannot hold a control. */}
                            <button
                              type="button"
                              aria-label={`Remove "${search.query}" from recent searches`}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromHistory(search.query);
                              }}
                              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </CommandGroup>
                    )}

                    {/* Smart suggestions when empty */}
                    {showSuggestions && !suggestionsLoading && (
                      <>
                        {/* My assigned items */}
                        {myItems.length > 0 && (
                          <>
                            {showRecentSearches && <CommandSeparator />}
                            <CommandGroup
                              heading={
                                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                                  <Sparkles className="h-4 w-4" />
                                  Assigned to Me
                                </span>
                              }
                            >
                              {myItems.map((item) => (
                                <div key={item.id}>
                                  <CompactSearchResultItem
                                    result={item}
                                    onSelect={handleSelect}
                                  />
                                </div>
                              ))}
                            </CommandGroup>
                          </>
                        )}

                        {/* Quick commands */}
                        {quickCommands.length > 0 && (
                          <>
                            <CommandSeparator />
                            <CommandGroup
                              heading={
                                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                                  <Zap className="h-4 w-4" />
                                  Quick Actions
                                </span>
                              }
                            >
                              {quickCommands.map((cmd) => (
                                <div key={cmd.id}>
                                  <CompactSearchResultItem
                                    result={cmd}
                                    onSelect={handleSelect}
                                  />
                                </div>
                              ))}
                            </CommandGroup>
                          </>
                        )}

                        {/* Go to Page */}
                        <CommandSeparator />
                        <CommandGroup
                          heading={
                            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                              <ArrowRight className="h-4 w-4" />
                              Go to Page
                            </span>
                          }
                        >
                          {GO_TO_PAGES.map((page) => (
                            <CommandItem
                              key={page.path}
                              value={pageOptionValue(page.path)}
                              className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-primary-tint rounded-lg transition-colors"
                              onSelect={() => {
                                navigate(page.path);
                                onOpenChange(false);
                                setQuery("");
                              }}
                            >
                              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
                                <page.icon className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <span className="text-sm font-medium">{page.label}</span>
                              <span className="ml-auto text-xs text-muted-foreground">{page.path}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>

                        {/* Recently updated */}
                        {recentItems.length > 0 && (
                          <>
                            <CommandSeparator />
                            <CommandGroup
                              heading={
                                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                                  <Clock className="h-4 w-4" />
                                  Recently Updated
                                </span>
                              }
                            >
                              {recentItems.slice(0, 5).map((item) => (
                                <div key={item.id}>
                                  <CompactSearchResultItem
                                    result={item}
                                    onSelect={handleSelect}
                                  />
                                </div>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                      </>
                    )}

                    {/* Search results grouped by category */}
                    {showResults && !isLoading && groupedResults && (
                      <>
                        {Object.entries(groupedResults).map(([groupKey, groupResults], groupIdx) => (
                          <React.Fragment key={groupKey}>
                            {groupIdx > 0 && <CommandSeparator />}
                            <CommandGroup heading={groupLabels[groupKey] || groupKey}>
                              {groupResults.map((result, idx) => {
                                return (
                                  <div key={result.id}>
                                    <EnhancedSearchResultItem
                                      result={result}
                                      isSelected={commandValue === getResultOptionValue(result)}
                                      onSelect={handleSelect}
                                      onQuickAction={handleQuickAction}
                                      highlightQuery={query}
                                      showQuickActions={!showPreview}
                                    />
                                  </div>
                                );
                              })}
                            </CommandGroup>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </CommandList>

                  {/* Footer with keyboard hints */}
                  <div className="sticky bottom-0 border-t border-border/60 px-5 py-3 hidden sm:flex items-center justify-between text-[11px] text-foreground/70 bg-surfaceRaised">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      {isSolomonMode ? (
                        <>
                          <span className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            <span className="font-semibold">Ask Solomon</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">↵</kbd>
                            <span className="font-medium">Execute</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">Esc</kbd>
                            <span className="font-medium">Close</span>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">↑↓</kbd>
                            <span className="font-medium">Navigate</span>
                          </span>
                          <span className="hidden sm:flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">Tab</kbd>
                            <span className="font-medium">Switch Filters</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">↵</kbd>
                            <span className="font-medium">Open</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <kbd className="px-2 py-1 rounded bg-background text-[10px] font-mono font-semibold border border-border shadow-sm">Esc</kbd>
                            <span className="font-medium">Close</span>
                          </span>
                        </>
                      )}
                      <span className="hidden sm:flex items-center gap-1.5 border-l border-border/40 pl-3 ml-1">
                        <span className="text-[13px]">🤖</span>
                        <span className="font-medium">Ask Solomon AI</span>
                        <kbd className="px-1.5 py-0.5 rounded bg-black/[0.05] text-[10px] font-mono font-semibold">⌘S</kbd>
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {!isSolomonMode && query.length >= 2 && groupedResults && (
                        <span className="text-[10px] font-medium text-foreground/60 hidden sm:inline">
                          {Object.keys(groupedResults).length} categor{Object.keys(groupedResults).length === 1 ? "y" : "ies"} • {results.length} result{results.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      {!isSolomonMode && results.length > 0 && query.length < 2 && (
                        <span className="font-semibold text-foreground/80 hidden sm:inline">{results.length} result{results.length !== 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </div>
                </Command>
              </div>

              {/* Preview panel - shows Solomon in Solomon mode, otherwise shows result preview */}
              {isSolomonMode ? (
                <div
                  className="hidden lg:block lg:min-w-[340px]"
                  style={{ width: 360, background: "rgba(248,246,243,0.5)" }}
                >
                  <InlineSolomonResponse
                    query={solomonQuery}
                    onExpand={handleExpandSolomon}
                    onClear={() => setQuery("?")}
                    onAsk={(q) => setQuery(`? ${q}`)}
                  />
                </div>
              ) : shouldShowDesktopPane ? (
                <div
                  className="hidden lg:flex lg:min-w-[340px] lg:flex-col"
                  style={{ width: 360, background: "rgba(248,246,243,0.5)" }}
                >
                  {paneResult ? (
                    <SearchPreviewPanel
                      result={paneResult}
                      onAction={handlePreviewAction}
                      onSelect={() => handleSelect(paneResult)}
                    />
                  ) : (
                    <div className="flex h-full flex-col justify-between p-6">
                      <div className="space-y-5">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-foreground/60">Preview</p>
                          <h3 className="mt-2 text-base font-semibold text-foreground">Selection details will appear here</h3>
                          <p className="mt-2 text-sm text-foreground/70">
                            Use ↑↓ to move through results, or hover an item to inspect contextual details before opening it.
                          </p>
                        </div>

                        <div className="rounded-xl border border-border/60 bg-background/70 p-4 space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-foreground/60">Quick help</p>
                          <div className="space-y-2 text-sm text-foreground/80">
                            <p><span className="font-semibold">Tab</span> cycles filters</p>
                            <p><span className="font-semibold">?</span> asks Solomon inline</p>
                            <p><span className="font-semibold">/</span> opens command actions</p>
                            <p><span className="font-semibold">p:</span> search people • <span className="font-semibold">t:</span> tasks</p>
                            <p><span className="font-semibold">r:</span> requests • <span className="font-semibold">c:</span> contributions</p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-border/60 pt-4 space-y-1.5">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-foreground/60">Current context</p>
                        <p className="text-sm text-foreground/80">Filter: <span className="font-semibold">{FILTER_TABS.find((tab) => tab.id === activeFilter)?.label ?? "All"}</span></p>
                        <p className="text-sm text-foreground/80">{results.length} result{results.length === 1 ? "" : "s"} available</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
      </DialogContent>
    </Dialog>
  );
}
